import { createClient } from '@supabase/supabase-js';
import type { 
  Gender, 
  AgeGroup, 
  LivingSituation, 
  HearingLevel, 
  VisionLevel,
  AnswerLevel,
} from '../constants';
// Imported from the module, not the barrel — this is a value import and the
// barrel pulls in the whole constants tree.
import { defaultBatteryAlertFlags } from '../constants/batteryAlerts';

// Supabase URL + anon key. Anon keys are publishable by design (RLS is
// what protects data — see server/migrations/2026-04-12-auth-rls.sql),
// so it's safe in the bundle. Required from env (REACT_APP_SUPABASE_*) —
// NO hardcoded fallback: with separate staging/prod projects, a missing
// value must fail fast at startup rather than silently connect to the
// wrong database.
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase config: set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY ' +
      '(see .env / .env.production). No fallback is provided by design — a misconfigured ' +
      'build must fail rather than silently connect to the wrong project.',
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    flowType: 'pkce',
    detectSessionInUrl: true,
    persistSession: true,
    autoRefreshToken: true,
  },
});

// =====================================================
// TYPE DEFINITIONS FOR NEW CONFIGURATION SCHEMA
// =====================================================

export interface Caregiver {
  id?: string;
  email: string;
  full_name: string;
  /** E.164 mobile phone for SMS alerts (F01). Nulled server-side after a
   *  Twilio 21614 (invalid mobile) so future sends short-circuit at no_phone. */
  phone?: string | null;
  /** True after the caregiver replied STOP (Twilio 21610). Future sends are
   *  short-circuited at the opted_out check. Dashboard surfaces the
   *  re-opt-in banner (#21) while this is true. */
  sms_opted_out?: boolean;
  /** Locale for SMS template selection. 'en' | 'fr'. */
  language?: 'en' | 'fr';
  /** Ticket 2: optional "How did you hear about AidFone?" answer captured at
   *  signup. Stable option key (see REFERRAL_SOURCE_OPTIONS) or null. */
  referral_source?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface Senior {
  id?: string;
  caregiver_id: string;
  name: string;
  sex_at_birth?: 'male' | 'female';
  photo_url?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Assessment {
  id?: string;
  senior_id: string;
  profile_type: 'cognitive' | 'physical' | 'visual';
  severity: 'severe' | 'moderate' | 'mild';
  score: number;
  answers: Record<number, number>; // question_id -> answer_value
  created_at?: string;
}

export interface Subscription {
  id?: string;
  senior_id: string;
  plan_tier: 'plus';
  price: number;
  status?: 'trial' | 'active' | 'cancelled' | 'expired';
  trial_ends_at?: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  created_at?: string;
  updated_at?: string;
}

export interface SeniorContact {
  id?: string;
  senior_id: string;
  name: string;
  phone: string;
  relationship: string;
  photo_url?: string;
  is_emergency?: boolean;
  aliases?: string[];
  sort_order?: number;
  created_at?: string;
}

export interface Configuration {
  id?: string;
  senior_id: string;
  layout_id: string;
  geo_fence_enabled?: boolean;
  geo_fence_address?: string;
  geo_fence_radius?: number;
  geo_fence_alert_on_exit?: boolean;
  health_medication_reminders?: boolean;
  health_daily_checkin?: boolean;
  health_checkin_time?: string;
  /** F05 — Voice Assistant 40% battery nag enabled. Default true; cognitive
   *  profiles default false per brief. NOT NULL on the DB side. */
  voice_nag_enabled?: boolean;
  /** F05 — Caregiver 25% lowbat SMS enabled. Default false. Must be true AND
   *  caregivers.sms_opted_out=false for the send-sms lowbat slot to fire. */
  lowbat_sms_enabled?: boolean;
  /** F04 — Caregiver receives SMS at 11:00 & 15:00 if voicemail is waiting.
   *  Default false per brief. Must be true AND voicemail_flag.pending=true AND
   *  caregivers.sms_opted_out=false for the scheduled-sms function to fire. */
  voicemail_sms_enabled?: boolean;
  activation_code?: string;
  activated_at?: string;
  /** PR #2 Managed Mode — device locks into the AidFone launcher; exiting
   *  requires the caregiver PIN. Default true (enabled). */
  managed_mode_enabled?: boolean;
  /** PR #2 Managed Mode — 4-digit caregiver PIN. Stored plain here; the device
   *  hashes it (salted SHA-256) on receipt and never keeps the plain value.
   *  NULL until the caregiver sets one. */
  managed_mode_pin?: string | null;
  /** Scam-call blocking. Default true; the caregiver can turn it off from the
   *  dashboard (this is what the FAQ's "turn it off anytime" claim rests on).
   *  Reaches the device via sync-config → Firebase. */
  call_screening_enabled?: boolean;
  created_at?: string;
  updated_at?: string;
}

// =====================================================
// PHASE 3: NEW TYPES FOR DECISION ENGINE
// =====================================================

// Senior Profile (Pre-screening data from P1-P5)
export interface SeniorProfile {
  id?: string;
  senior_id: string;
  gender: Gender;
  age_group: AgeGroup;
  living_situation: LivingSituation;
  hearing: HearingLevel;
  vision: VisionLevel;
  created_at?: string;
  updated_at?: string;
}

// Profile Answers (A/B/C format from profile-specific questions)
export interface ProfileAnswersRecord {
  q1: AnswerLevel;
  q2: AnswerLevel;
  q3: AnswerLevel;
}

// Recommendation level for features
export type RecommendationLevel = null | 'standard' | 'highly';

// Feature configuration with enabled state and recommendation
export interface FeatureConfig {
  id?: string;
  senior_id: string;
  // Features with their enabled state and recommendation level
  voice_assistant_enabled: boolean;
  voice_assistant_recommended: RecommendationLevel;
  gps_location_enabled: boolean;
  gps_location_recommended: RecommendationLevel;
  geofencing_enabled: boolean;
  geofencing_recommended: RecommendationLevel;
  fall_detection_enabled: boolean;
  fall_detection_recommended: RecommendationLevel;
  health_companion_enabled: boolean;
  health_companion_recommended: RecommendationLevel;
  // Standard features (always included, no recommendation)
  sos_button_enabled: boolean;
  photo_contacts_enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

// Risk level from decision engine
export type RiskLevel = 'LOW' | 'MODERATE' | 'HIGH' | 'VERY_HIGH';

// Layout configuration from decision engine
export interface LayoutConfig {
  layout_type: string;
  grid_size: '2x2' | '2x3' | '3x4';
  font_size: 'large' | 'extra_large';
  button_style: 'standard' | 'high_contrast';
}

// Full configuration with all related data
export interface FullSeniorConfiguration {
  senior: Senior;
  assessment: Assessment;
  subscription: Subscription;
  contacts: SeniorContact[];
  configuration: Configuration;
  // New Phase 3 additions
  seniorProfile?: SeniorProfile;
  featureConfig?: FeatureConfig;
}

// =====================================================
// STORAGE FUNCTIONS (Supabase Storage)
// =====================================================

const STORAGE_BUCKET = 'photos';

export async function uploadContactPhoto(file: File): Promise<string> {
  const ext = file.name.split('.').pop() || 'jpg';
  const path = `contacts/${crypto.randomUUID()}.${ext}`;

  const { error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (error) {
    console.error('Error uploading contact photo:', error);
    throw error;
  }

  const { data } = supabase.storage
    .from(STORAGE_BUCKET)
    .getPublicUrl(path);

  return data.publicUrl;
}

export async function deletePhoto(publicUrl: string): Promise<void> {
  // Extract the path from the public URL
  const bucketPath = publicUrl.split(`/storage/v1/object/public/${STORAGE_BUCKET}/`)[1];
  if (!bucketPath) return;

  const { error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .remove([bucketPath]);

  if (error) {
    console.error('Error deleting photo:', error);
    throw error;
  }
}

// =====================================================
// CAREGIVER FUNCTIONS
// =====================================================

export async function createCaregiver(caregiver: Omit<Caregiver, 'id' | 'created_at' | 'updated_at'>): Promise<Caregiver | null> {
  const { data, error } = await supabase
    .from('caregivers')
    .insert([caregiver])
    .select()
    .single();

  if (error) {
    console.error('Error creating caregiver:', error);
    throw error;
  }
  return data;
}

export async function getCaregiverByEmail(email: string): Promise<Caregiver | null> {
  const { data, error } = await supabase
    .from('caregivers')
    .select('*')
    .eq('email', email)
    .single();

  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching caregiver:', error);
    throw error;
  }
  return data;
}

export async function getOrCreateCaregiver(email: string, full_name: string): Promise<Caregiver> {
  let caregiver = await getCaregiverByEmail(email);
  if (!caregiver) {
    caregiver = await createCaregiver({ email, full_name });
  }
  return caregiver!;
}

/**
 * Create a caregiver row whose id matches the Supabase Auth user id.
 * This is the B2C convention: caregivers.id === auth.users.id so RLS
 * policies can filter by `auth.uid()` without a join.
 * Called from the Deployment step right after supabase.auth.signUp().
 */
export async function createCaregiverWithAuthId(params: {
  authUserId: string;
  email: string;
  fullName: string;
  /** E.164 phone for SMS alerts (F01). Optional; null means caregiver opted out of SMS. */
  phone?: string | null;
  /** Ticket 2: optional "How did you hear about AidFone?" answer (stable option key). */
  referralSource?: string | null;
}): Promise<Caregiver> {
  const { data, error } = await supabase
    .from('caregivers')
    .insert([
      {
        id: params.authUserId,
        email: params.email,
        full_name: params.fullName,
        phone: params.phone ?? null,
        referral_source: params.referralSource ?? null,
      },
    ])
    .select()
    .single();

  if (error) {
    console.error('Error creating caregiver with auth id:', error);
    throw error;
  }
  return data;
}

// =====================================================
// SENIOR FUNCTIONS
// =====================================================

export async function createSenior(senior: Omit<Senior, 'id' | 'created_at' | 'updated_at'>): Promise<Senior | null> {
  const { data, error } = await supabase
    .from('seniors')
    .insert([senior])
    .select()
    .single();

  if (error) {
    console.error('Error creating senior:', error);
    throw error;
  }
  return data;
}

export async function getSeniorsByCaregiver(caregiverId: string): Promise<Senior[]> {
  const { data, error } = await supabase
    .from('seniors')
    .select('*')
    .eq('caregiver_id', caregiverId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching seniors:', error);
    throw error;
  }
  return data || [];
}

export async function getAllSeniors(): Promise<Senior[]> {
  const { data, error } = await supabase
    .from('seniors')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching all seniors:', error);
    throw error;
  }
  return data || [];
}

export async function getSeniorById(id: string): Promise<Senior | null> {
  const { data, error } = await supabase
    .from('seniors')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching senior:', error);
    throw error;
  }
  return data;
}

export async function updateSenior(id: string, updates: Partial<Pick<Senior, 'name' | 'photo_url'>>): Promise<Senior | null> {
  const { data, error } = await supabase
    .from('seniors')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating senior:', error);
    throw error;
  }
  return data;
}

export async function deleteSenior(id: string): Promise<void> {
  // Use RPC function to bypass RLS policies
  const { error } = await supabase.rpc('delete_senior', { senior_id_input: id });

  if (error) {
    console.error('Error deleting senior:', error);
    throw error;
  }
}

// =====================================================
// ASSESSMENT FUNCTIONS
// =====================================================

export async function createAssessment(assessment: Omit<Assessment, 'id' | 'created_at'>): Promise<Assessment | null> {
  const { data, error } = await supabase
    .from('assessments')
    .insert([assessment])
    .select()
    .single();

  if (error) {
    console.error('Error creating assessment:', error);
    throw error;
  }
  return data;
}

export async function getAssessmentBySenior(seniorId: string): Promise<Assessment | null> {
  const { data, error } = await supabase
    .from('assessments')
    .select('*')
    .eq('senior_id', seniorId)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching assessment:', error);
    throw error;
  }
  return data;
}

// =====================================================
// SUBSCRIPTION FUNCTIONS
// =====================================================

export async function createSubscription(subscription: Omit<Subscription, 'id' | 'created_at' | 'updated_at'>): Promise<Subscription | null> {
  // Set trial end date to 30 days from now
  const trialEndsAt = new Date();
  trialEndsAt.setDate(trialEndsAt.getDate() + 30);

  const { data, error } = await supabase
    .from('subscriptions')
    .insert([{
      ...subscription,
      status: 'trial',
      trial_ends_at: trialEndsAt.toISOString()
    }])
    .select()
    .single();

  if (error) {
    console.error('Error creating subscription:', error);
    throw error;
  }
  return data;
}

export async function getSubscriptionBySenior(seniorId: string): Promise<Subscription | null> {
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .eq('senior_id', seniorId)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching subscription:', error);
    throw error;
  }
  return data;
}

// =====================================================
// SENIOR CONTACTS FUNCTIONS
// =====================================================

export async function createSeniorContacts(contacts: Omit<SeniorContact, 'id' | 'created_at'>[]): Promise<SeniorContact[]> {
  if (contacts.length === 0) return [];

  const { data, error } = await supabase
    .from('senior_contacts')
    .insert(contacts)
    .select();

  if (error) {
    console.error('Error creating contacts:', error);
    throw error;
  }
  return data || [];
}

export async function getContactsBySenior(seniorId: string): Promise<SeniorContact[]> {
  const { data, error } = await supabase
    .from('senior_contacts')
    .select('*')
    .eq('senior_id', seniorId)
    .order('sort_order', { ascending: true });

  if (error) {
    console.error('Error fetching contacts:', error);
    throw error;
  }
  return data || [];
}

export async function deleteContactsBySenior(seniorId: string): Promise<void> {
  const { error } = await supabase
    .from('senior_contacts')
    .delete()
    .eq('senior_id', seniorId);

  if (error) {
    console.error('Error deleting contacts:', error);
    throw error;
  }
}

/**
 * Update all contacts for a senior (replaces existing contacts)
 */
export async function updateSeniorContacts(
  seniorId: string,
  contacts: Array<{
    id?: string;
    name: string;
    phone: string;
    relationship: string;
    photo_url?: string;
    is_emergency?: boolean;
    aliases?: string[];
  }>
): Promise<SeniorContact[]> {
  try {
    // Get existing contacts from database
    const { data: existingContacts, error: fetchError } = await supabase
      .from('senior_contacts')
      .select('*')
      .eq('senior_id', seniorId);

    if (fetchError) {
      console.error('Error fetching existing contacts:', fetchError);
      throw fetchError;
    }

    // Determine what to update, insert, and delete
    const existingIds = new Set((existingContacts || []).map(c => c.id));
    const incomingIds = new Set(contacts.filter(c => c.id).map(c => c.id!));

    const toDelete = (existingContacts || []).filter(c => !incomingIds.has(c.id));
    const toInsert = contacts.filter(c => !c.id);
    const toUpdate = contacts.filter(c => c.id && existingIds.has(c.id));

    // Delete removed contacts
    if (toDelete.length > 0) {
      for (const contact of toDelete) {
        const { error: delError } = await supabase
          .from('senior_contacts')
          .delete()
          .eq('id', contact.id);

        if (delError) {
          console.error(`Failed to delete contact ${contact.id}:`, delError);
        }
      }
    }

    // Update existing contacts
    if (toUpdate.length > 0) {
      for (let i = 0; i < toUpdate.length; i++) {
        const contact = toUpdate[i];
        const { error: updateError } = await supabase
          .from('senior_contacts')
          .update({
            name: contact.name,
            phone: contact.phone,
            relationship: contact.relationship,
            photo_url: contact.photo_url,
            is_emergency: contact.is_emergency || false,
            aliases: contact.aliases || [],
            sort_order: i,
          })
          .eq('id', contact.id!);

        if (updateError) {
          console.error(`Failed to update contact ${contact.id}:`, updateError);
        }
      }
    }

    // Insert new contacts
    if (toInsert.length > 0) {
      const contactsToCreate = toInsert.map((c, idx) => ({
        senior_id: seniorId,
        name: c.name,
        phone: c.phone,
        relationship: c.relationship,
        photo_url: c.photo_url,
        is_emergency: c.is_emergency || false,
        aliases: c.aliases || [],
        sort_order: toUpdate.length + idx,
      }));

      const { error: insertError } = await supabase
        .from('senior_contacts')
        .insert(contactsToCreate)
        .select();

      if (insertError) {
        console.error('Error inserting new contacts:', insertError);
        throw insertError;
      }
    }

    // Fetch and return final state
    const { data: finalContacts, error: finalError } = await supabase
      .from('senior_contacts')
      .select('*')
      .eq('senior_id', seniorId)
      .order('sort_order', { ascending: true });

    if (finalError) {
      console.error('Error fetching final contacts:', finalError);
      throw finalError;
    }

    return finalContacts || [];
  } catch (error) {
    console.error('Error in updateSeniorContacts:', error);
    throw error;
  }
}

// =====================================================
// CONFIGURATION FUNCTIONS
// =====================================================

export async function createConfiguration(config: Omit<Configuration, 'id' | 'created_at' | 'updated_at'>): Promise<Configuration | null> {
  const { data, error } = await supabase
    .from('configurations')
    .insert([config])
    .select()
    .single();

  if (error) {
    console.error('Error creating configuration:', error);
    throw error;
  }
  return data;
}

export async function getConfigurationBySenior(seniorId: string): Promise<Configuration | null> {
  const { data, error } = await supabase
    .from('configurations')
    .select('*')
    .eq('senior_id', seniorId)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching configuration:', error);
    throw error;
  }
  return data;
}

export async function getConfigurationByActivationCode(code: string): Promise<Configuration | null> {
  const { data, error } = await supabase
    .from('configurations')
    .select('*')
    .eq('activation_code', code)
    .single();

  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching configuration by code:', error);
    throw error;
  }
  return data;
}

export async function activateConfiguration(configId: string): Promise<Configuration | null> {
  const { data, error } = await supabase
    .from('configurations')
    .update({ activated_at: new Date().toISOString() })
    .eq('id', configId)
    .select()
    .single();

  if (error) {
    console.error('Error activating configuration:', error);
    throw error;
  }
  return data;
}

/**
 * F05 — update the two battery-alert toggles on a senior's configuration.
 * Either flag is optional; pass only the ones that changed.
 */
export async function updateBatteryAlertFlags(
  seniorId: string,
  patch: { voice_nag_enabled?: boolean; lowbat_sms_enabled?: boolean }
): Promise<Configuration | null> {
  const { data, error } = await supabase
    .from('configurations')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('senior_id', seniorId)
    .select()
    .single();
  if (error) {
    console.error('Error updating battery alert flags:', error);
    throw error;
  }
  return data;
}

/**
 * F04 — update the voicemail SMS toggle on a senior's configuration. Same
 * shape as [updateBatteryAlertFlags] — kept separate so the call sites stay
 * narrowly typed (a Voicemail drawer can't accidentally write a battery flag).
 */
export async function updateVoicemailFlags(
  seniorId: string,
  patch: { voicemail_sms_enabled?: boolean }
): Promise<Configuration | null> {
  const { data, error } = await supabase
    .from('configurations')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('senior_id', seniorId)
    .select()
    .single();
  if (error) {
    console.error('Error updating voicemail flags:', error);
    throw error;
  }
  return data;
}

/**
 * Launch Tracker line 6 — flip scam-call blocking for a senior. The device
 * persists the flag locally, so it keeps working with no network at call time.
 */
export async function updateCallScreening(
  seniorId: string,
  enabled: boolean
): Promise<Configuration | null> {
  const { data, error } = await supabase
    .from('configurations')
    .update({ call_screening_enabled: enabled, updated_at: new Date().toISOString() })
    .eq('senior_id', seniorId)
    .select()
    .single();
  if (error) {
    console.error('Error updating call screening:', error);
    throw error;
  }
  return data;
}

/**
 * PR #2 Managed Mode — update the caregiver PIN and/or the enabled flag on a
 * senior's configuration. Pass only the fields that changed. The PIN is stored
 * here as plain text; the device hashes it (salted SHA-256) on receipt via the
 * sync-config → Firebase path and never persists the plain value.
 */
export async function updateManagedMode(
  seniorId: string,
  patch: { managed_mode_pin?: string | null; managed_mode_enabled?: boolean }
): Promise<Configuration | null> {
  const { data, error } = await supabase
    .from('configurations')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('senior_id', seniorId)
    .select()
    .single();
  if (error) {
    console.error('Error updating managed mode:', error);
    throw error;
  }
  return data;
}

/* ============================================================================
 * F06 — Medication Reminders
 *
 * Brief: max 8 active per senior (enforced at DB level via trigger), 4-label
 * dropdown (morning/noon/afternoon/evening), 12-hour time + AM/PM. Loi 25:
 * no drug names, no dosages, no adherence.
 * ========================================================================== */

export type MedicationLabel = 'morning' | 'noon' | 'afternoon' | 'evening';
export type Ampm = 'AM' | 'PM';

export interface MedicationReminder {
  id: string;
  senior_user_id: string;
  caregiver_user_id: string;
  hour: number;          // 1..12
  minute: number;        // 0..59
  ampm: Ampm;
  label: MedicationLabel;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export async function listMedicationReminders(seniorId: string): Promise<MedicationReminder[]> {
  const { data, error } = await supabase
    .from('medication_reminders')
    .select('*')
    .eq('senior_user_id', seniorId)
    .order('hour', { ascending: true })
    .order('minute', { ascending: true });
  if (error) {
    console.error('listMedicationReminders failed:', error);
    throw error;
  }
  return (data ?? []) as MedicationReminder[];
}

export async function addMedicationReminder(
  args: { seniorId: string; caregiverId: string; hour: number; minute: number; ampm: Ampm; label: MedicationLabel },
): Promise<MedicationReminder> {
  const { data, error } = await supabase
    .from('medication_reminders')
    .insert({
      senior_user_id: args.seniorId,
      caregiver_user_id: args.caregiverId,
      hour: args.hour,
      minute: args.minute,
      ampm: args.ampm,
      label: args.label,
      active: true,
    })
    .select()
    .single();
  if (error) {
    // The DB trigger raises `max_8_active_reminders_per_senior` when the cap
    // is hit. Surface it untouched so the UI can show the right message.
    console.error('addMedicationReminder failed:', error);
    throw error;
  }
  return data as MedicationReminder;
}

export async function removeMedicationReminder(id: string): Promise<void> {
  const { error } = await supabase
    .from('medication_reminders')
    .delete()
    .eq('id', id);
  if (error) {
    console.error('removeMedicationReminder failed:', error);
    throw error;
  }
}

/** Used by the "Re-enable reminders" banner after the senior voice-paused. */
export async function reactivateAllMedicationReminders(seniorId: string): Promise<void> {
  const { error } = await supabase
    .from('medication_reminders')
    .update({ active: true })
    .eq('senior_user_id', seniorId);
  if (error) {
    console.error('reactivateAllMedicationReminders failed:', error);
    throw error;
  }
}

export async function updateConfigurationLayout(seniorId: string, layoutId: string): Promise<Configuration | null> {
  const { data, error } = await supabase
    .from('configurations')
    .update({ layout_id: layoutId, updated_at: new Date().toISOString() })
    .eq('senior_id', seniorId)
    .select()
    .single();

  if (error) {
    console.error('Error updating configuration layout:', error);
    throw error;
  }
  return data;
}

// =====================================================
// FULL CONFIGURATION SAVE (from wizard)
// =====================================================

export interface WizardData {
  /**
   * Auth user id — equals caregivers.id per the B2C auth model.
   * Created during the Deployment step's signup flow (supabase.auth.signUp).
   * See docs/plans/2026-04-12-b2c-auth-dashboard-redesign.md Task 12.
   */
  caregiverId: string;
  seniorName: string;
  seniorSexAtBirth?: 'male' | 'female';
  profileType: 'cognitive' | 'physical' | 'visual';
  severity: 'severe' | 'moderate' | 'mild';
  assessmentScore: number;
  assessmentAnswers: Record<number, number>;
  planTier: 'plus';
  planPrice: number;
  layoutId: string;
  contacts: Array<{
    name: string;
    phone: string;
    relationship: string;
    photoUrl?: string;
    isEmergency: boolean;
    aliases?: string[];
  }>;
  // REMOVED: geoFenceEnabled / Lat / Lng / Radius / Schedule / AlertOnExit.
  // Geofencing is CUT from v1 (Feature 03 spec); the wizard no longer collects
  // a zone and createConfiguration() below no longer writes the geo_fence_*
  // columns. The Configuration interface keeps its optional geo_fence_* fields
  // because the columns still exist in both Supabase projects — this is about
  // what the signup path writes, not about the schema.
  healthMedicationReminders?: boolean;
  healthDailyCheckin?: boolean;
  healthCheckinTime?: string;
  activationCode: string;
  
  // =========================================
  // PHASE 3: New Decision Engine Fields
  // =========================================
  
  // Pre-screening answers (P1-P5)
  preScreening?: {
    gender: Gender;
    ageGroup: AgeGroup;
    livingSituation: LivingSituation;
    hearing: HearingLevel;
    vision: VisionLevel;
  };
  
  // Profile-specific A/B/C answers
  profileAnswers?: ProfileAnswersRecord;
  
  // Risk level from decision engine
  riskLevel?: RiskLevel;
  
  // Feature recommendations and enabled states
  features?: {
    voiceAssistant: { enabled: boolean; recommended: RecommendationLevel };
    gpsLocation: { enabled: boolean; recommended: RecommendationLevel };
    geofencing: { enabled: boolean; recommended: RecommendationLevel };
    fallDetection: { enabled: boolean; recommended: RecommendationLevel };
    healthCompanion: { enabled: boolean; recommended: RecommendationLevel };
    sosButton: { enabled: boolean };
    photoContacts: { enabled: boolean };
  };
  
  // Layout recommendation from decision engine
  layout?: {
    layoutType: string;
    gridSize: '2x2' | '2x3' | '3x4';
    fontSize: 'large' | 'extra_large';
    buttonStyle: 'standard' | 'high_contrast';
  };
}

export async function saveFullConfiguration(wizardData: WizardData): Promise<FullSeniorConfiguration> {
  // Caregiver row is created during signup (createCaregiverWithAuthId) before this runs.
  // We use wizardData.caregiverId directly as the auth.users.id / caregivers.id.
  const senior = await createSenior({
    caregiver_id: wizardData.caregiverId,
    name: wizardData.seniorName,
    ...(wizardData.seniorSexAtBirth && { sex_at_birth: wizardData.seniorSexAtBirth }),
  });

  if (!senior) throw new Error('Failed to create senior');

  // 3-6. Create assessment, subscription, contacts, and configuration in parallel
  const contactsToCreate = wizardData.contacts.map((c, idx) => ({
    senior_id: senior.id!,
    name: c.name,
    phone: c.phone,
    relationship: c.relationship,
    photo_url: c.photoUrl,
    is_emergency: c.isEmergency,
    aliases: c.aliases || [],
    sort_order: idx,
  }));

  const [assessment, subscription, contacts, configuration] = await Promise.all([
    createAssessment({
      senior_id: senior.id!,
      profile_type: wizardData.profileType,
      severity: wizardData.severity,
      score: wizardData.assessmentScore,
      answers: wizardData.assessmentAnswers,
    }),
    createSubscription({
      senior_id: senior.id!,
      plan_tier: wizardData.planTier,
      price: wizardData.planPrice,
    }),
    createSeniorContacts(contactsToCreate),
    createConfiguration({
      senior_id: senior.id!,
      layout_id: wizardData.layoutId,
      // REMOVED: geo_fence_enabled / _address / _radius / _alert_on_exit.
      //
      // Geofencing is CUT from v1 (Feature 03 spec). Omitting these four keys
      // leaves the columns to their own DB defaults on insert
      // (false / NULL / 500 / true) — the columns are NOT dropped, in either
      // Supabase project.
      //
      // Note for Wave 1.5: `geo_fence_address` never held an address. This
      // insert packed the map pin into it as `${lat},${lng}`, which is why the
      // live rows contain coordinate pairs in a text column named "address".
      // And the monitoring schedule had no column at all — it was accepted in
      // the wizard and silently dropped here. Fix both before restoring.
      health_medication_reminders: wizardData.healthMedicationReminders,
      health_daily_checkin: wizardData.healthDailyCheckin,
      health_checkin_time: wizardData.healthCheckinTime,
      // F05 launch spec — both battery-alert layers ship ON (voice nag OFF for
      // memory profiles). Set explicitly rather than leaning on the column
      // defaults: `lowbat_sms_enabled` still defaults false at the DB level, and
      // the per-profile voice-nag rule can't be expressed as a column default at
      // all. See constants/batteryAlerts.ts.
      ...defaultBatteryAlertFlags(wizardData.profileType),
      activation_code: wizardData.activationCode,
    }),
  ]);

  if (!assessment) throw new Error('Failed to create assessment');
  if (!subscription) throw new Error('Failed to create subscription');
  if (!configuration) throw new Error('Failed to create configuration');

  return {
    senior,
    assessment,
    subscription,
    contacts,
    configuration,
  };
}

// =====================================================
// GUARDIAN WAITLIST FUNCTIONS
// =====================================================

export interface GuardianWaitlistEntry {
  id?: string;
  email: string;
  created_at?: string;
}

// No `.select()` on these inserts — see the RETURNING note above
// createWizardSession. guardian_waitlist has no anon SELECT policy either, so a
// read-back here fails exactly the same way.
export async function addToGuardianWaitlist(email: string): Promise<void> {
  const { error } = await supabase
    .from('guardian_waitlist')
    .insert([{ email }]);

  if (error) {
    // Check if it's a duplicate email error
    if (error.code === '23505') {
      throw new Error('already_subscribed');
    }
    console.error('Error adding to Guardian waitlist:', error);
    throw error;
  }
}

// `checkGuardianWaitlistEmail` was removed here. It had no callers, and it could
// not have worked if it had any: it SELECTed guardian_waitlist, which anon cannot
// read, so it returned PGRST116 → `false` for every address including ones
// already on the list. The UNIQUE constraint on email is the real duplicate
// check — it surfaces as 23505 → 'already_subscribed' in the two functions here.

// =====================================================
// DISTRESS DETECTION WAITLIST FUNCTIONS
// =====================================================

export async function addToDistressDetectionWaitlist(email: string): Promise<void> {
  // Reuses the guardian_waitlist table, which is (id, email, created_at) — there
  // is no `source` column in either Supabase project. This insert used to send
  // one, which PostgREST rejected outright with PGRST204 regardless of RLS.
  // ⚠️ Consequence: the two waitlists are NOT distinguishable in the data. If
  // that matters, it needs a real `source` column added to BOTH projects first.
  const { error } = await supabase
    .from('guardian_waitlist')
    .insert([{ email }]);

  if (error) {
    if (error.code === '23505') {
      throw new Error('already_subscribed');
    }
    console.error('Error adding to Distress Detection waitlist:', error);
    throw error;
  }
}

// =====================================================
// PARTNER INQUIRY FUNCTIONS
// =====================================================

export interface PartnerInquiry {
  id?: string;
  name: string;
  company: string;
  email: string;
  phone?: string;
  message?: string;
  created_at?: string;
}

// No `.select()` — same RETURNING constraint as the waitlist inserts above.
export async function submitPartnerInquiry(inquiry: Omit<PartnerInquiry, 'id' | 'created_at'>): Promise<void> {
  const { error } = await supabase
    .from('partner_inquiries')
    .insert([inquiry]);

  if (error) {
    console.error('Error submitting partner inquiry:', error);
    throw error;
  }
}

// =====================================================
// DASHBOARD FUNCTIONS
// =====================================================

export interface DashboardSenior {
  senior: Senior;
  assessment: Assessment | null;
  subscription: Subscription | null;
  configuration: Configuration | null;
  contactCount: number;
}

export async function getDashboardData(caregiverId: string): Promise<DashboardSenior[]> {
  const seniors = await getSeniorsByCaregiver(caregiverId);

  const dashboardData: DashboardSenior[] = [];

  for (const senior of seniors) {
    const [assessment, subscription, configuration, contacts] = await Promise.all([
      getAssessmentBySenior(senior.id!).catch(() => null),
      getSubscriptionBySenior(senior.id!).catch(() => null),
      getConfigurationBySenior(senior.id!).catch(() => null),
      getContactsBySenior(senior.id!).catch(() => []),
    ]);

    dashboardData.push({
      senior,
      assessment,
      subscription,
      configuration,
      contactCount: contacts.length,
    });
  }

  return dashboardData;
}

export async function getAllDashboardData(): Promise<DashboardSenior[]> {
  const seniors = await getAllSeniors();

  const dashboardData: DashboardSenior[] = [];

  for (const senior of seniors) {
    const [assessment, subscription, configuration, contacts] = await Promise.all([
      getAssessmentBySenior(senior.id!).catch(() => null),
      getSubscriptionBySenior(senior.id!).catch(() => null),
      getConfigurationBySenior(senior.id!).catch(() => null),
      getContactsBySenior(senior.id!).catch(() => []),
    ]);

    dashboardData.push({
      senior,
      assessment,
      subscription,
      configuration,
      contactCount: contacts.length,
    });
  }

  return dashboardData;
}

// =====================================================
// WIZARD SESSION TRACKING FUNCTIONS
// Save pre-screening & assessment data immediately with IP tracking
// =====================================================

export interface WizardSession {
  id?: string;
  session_id: string;
  ip_address?: string;
  user_agent?: string;
  referrer?: string;
  senior_id?: string;
  configuration_id?: string;
  status?: 'started' | 'prescreening_complete' | 'assessment_complete' | 'completed';
  created_at?: string;
  updated_at?: string;
  completed_at?: string;
}

export interface PrescreeningSubmission {
  id?: string;
  session_id: string;
  gender?: string;
  age_group?: string;
  living_situation?: string;
  hearing?: string;
  vision?: string;
  answers_json?: Record<string, unknown>;
  created_at?: string;
}

export interface AssessmentSubmission {
  id?: string;
  session_id: string;
  profile_type: string;
  severity?: string;
  score?: number;
  q1_answer?: number;
  q2_answer?: number;
  q3_answer?: number;
  q4_answer?: number;
  answers_json?: Record<string, unknown>;
  created_at?: string;
}

// ⚠️ NEVER add `.select()` to an anon insert on the wizard or marketing tables.
//
// `.insert(...).select()` makes PostgREST emit `INSERT ... RETURNING`, and under
// RLS a RETURNING clause ALSO requires the new row to pass a **SELECT** policy.
// The 2026-07-27 hardening removed the anon SELECT policies from these tables, so
// every such insert has failed with 42501 ("new row violates row-level security
// policy") in BOTH Supabase projects since that date — silently, because the
// callers only warned. The INSERT policies themselves are intact and permissive
// (`WITH CHECK (true)`); it is the RETURNING read-back that is denied.
//
// A bare insert with no `.select()` sends `Prefer: return=minimal`, generates no
// RETURNING, and succeeds. Keep it that way. The fix is client-side on purpose:
// re-adding anon SELECT policies would hand anonymous callers read access to
// every wizard session, prescreening answer and assessment score in the table.
//
// Nothing needs the row back — `session_id` is a client-generated UUID v4 from
// `getOrCreateSessionId()` (src/lib/session.ts), and that is the key every later
// write uses. The DB-generated `id` surrogate is read by nobody.

/**
 * Create a new wizard session with client info
 * Called when user starts the configuration wizard
 */
export async function createWizardSession(
  sessionId: string,
  clientInfo: { ip: string; userAgent: string; referrer: string | null }
): Promise<void> {
  // The pre-flight "does this session already exist?" SELECT that used to sit
  // here was dead code for the same reason: anon has no SELECT policy, so it
  // always returned null and the early-return never fired. The UNIQUE constraint
  // on session_id already gives us the same guarantee, below, in one round-trip.
  const { error } = await supabase
    .from('wizard_sessions')
    .insert([{
      session_id: sessionId,
      ip_address: clientInfo.ip,
      user_agent: clientInfo.userAgent,
      referrer: clientInfo.referrer,
      status: 'started',
    }]);

  if (error) {
    // wizard_sessions.session_id is UNIQUE. A duplicate means the row is already
    // there — second tab, reload, or a concurrent request — which is the state we
    // wanted. Not a failure.
    if (error.code === '23505') {
      console.log('[WizardSession] Session already exists:', sessionId);
      return;
    }
    console.error('Error creating wizard session:', error);
    throw error;
  }
}

/**
 * Update wizard session status
 */
export async function updateWizardSessionStatus(
  sessionId: string,
  status: WizardSession['status']
): Promise<void> {
  const { error } = await supabase
    .from('wizard_sessions')
    .update({ status })
    .eq('session_id', sessionId);

  if (error) {
    console.error('Error updating wizard session status:', error);
    throw error;
  }
}

/**
 * Save pre-screening submission immediately
 * Called when user completes pre-screening (P1-P5)
 */
export async function savePrescreeningSubmission(
  sessionId: string,
  answers: {
    gender?: string;
    ageGroup?: string;
    livingSituation?: string;
    hearing?: string;
    vision?: string;
  }
): Promise<void> {
  // No `.select()` — see the RETURNING note above createWizardSession.
  const { error } = await supabase
    .from('prescreening_submissions')
    .insert([{
      session_id: sessionId,
      gender: answers.gender,
      age_group: answers.ageGroup,
      living_situation: answers.livingSituation,
      hearing: answers.hearing,
      vision: answers.vision,
      answers_json: answers,
    }]);

  if (error) {
    console.error('Error saving prescreening submission:', error);
    throw error;
  }

  // Update session status
  await updateWizardSessionStatus(sessionId, 'prescreening_complete');
}

/**
 * Save assessment submission immediately
 * Called when user completes the 4-question assessment
 */
export async function saveAssessmentSubmission(
  sessionId: string,
  profileType: string,
  answers: Array<{ questionId: number; value: number }>,
  severity?: string,
  score?: number
): Promise<void> {
  // Extract individual question answers
  const q1 = answers.find(a => a.questionId === 1)?.value;
  const q2 = answers.find(a => a.questionId === 2)?.value;
  const q3 = answers.find(a => a.questionId === 3)?.value;
  const q4 = answers.find(a => a.questionId === 4)?.value;

  // No `.select()` — see the RETURNING note above createWizardSession.
  const { error } = await supabase
    .from('assessment_submissions')
    .insert([{
      session_id: sessionId,
      profile_type: profileType,
      severity,
      score,
      q1_answer: q1,
      q2_answer: q2,
      q3_answer: q3,
      q4_answer: q4,
      answers_json: { answers, severity, score },
    }]);

  if (error) {
    console.error('Error saving assessment submission:', error);
    throw error;
  }

  // Update session status
  await updateWizardSessionStatus(sessionId, 'assessment_complete');
}

/**
 * Link wizard session to the final senior and configuration IDs
 * Called after successful deployment
 */
export async function linkSessionToConfiguration(
  sessionId: string,
  seniorId: string,
  configurationId: string
): Promise<void> {
  const { error } = await supabase
    .from('wizard_sessions')
    .update({
      senior_id: seniorId,
      configuration_id: configurationId,
      status: 'completed',
      completed_at: new Date().toISOString(),
    })
    .eq('session_id', sessionId);

  if (error) {
    console.error('Error linking session to configuration:', error);
    throw error;
  }
}

/**
 * Get wizard session by session ID
 */
export async function getWizardSession(sessionId: string): Promise<WizardSession | null> {
  const { data, error } = await supabase
    .from('wizard_sessions')
    .select('*')
    .eq('session_id', sessionId)
    .single();

  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching wizard session:', error);
    throw error;
  }
  return data;
}
